<template>
  <div
    v-if="isRendered"
    class="cancel-inspection-request-dialog"
    :class="{ 'cancel-inspection-request-dialog--open': isShown }"
    role="dialog"
    aria-modal="true"
  >
    <div class="cancel-inspection-request-dialog__backdrop" @click.self="close">
      <div
        class="cancel-inspection-request-dialog__panel cancel-inspection-request-dialog__popup cancel-inspection-request-dialog--bg-pattern"
        :style="{ width: '500px' }"
        @click.stop
      >
        <div class="cancel-inspection-request-dialog__header">
          <span class="cancel-inspection-request-dialog__icon-wrapper">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path opacity="0.4" d="M13.3346 9.16667C13.3346 6.86548 11.4692 5 9.16797 5" stroke="black" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z" stroke="black" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>

          <div class="cancel-inspection-request-dialog__spacer" />

          <button
            type="button"
            class="cancel-inspection-request-dialog__close-button"
            aria-label="Close dialog"
            @click="close"
          >
            <Icon class="cancel-inspection-request-dialog__cursor-pointer" name="popup_close" />
          </button>
        </div>

        <div class="cancel-inspection-request-dialog__content">
          <h3 class="cancel-inspection-request-dialog__title" :class="{ 'text-center': center }">
            {{ resolvedTitle }}
          </h3>
          <div
            v-if="resolvedMessageHtml"
            v-html="resolvedMessageHtml"
            class="cancel-inspection-request-dialog__message"
            :class="{ 'text-center': center }"
          ></div>
        </div>

        <div class="cancel-inspection-request-dialog__footer">
          <StandardButton
            :loaderLeft="$loader.isAppLoading"
            size="full-width"
            :type="buttonType"
            :label="actionLabel"
            :disabled="$loader.isAppLoading"
            @click="emit('onAction')"
            class="cancel-inspection-request-dialog__button"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

const ANIMATION_MS = 180;

const props = defineProps({
  value: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ""
  },
  message: {
    type: String,
    default: ""
  },
  actionLabel: {
    type: String,
    default: "Cancel"
  },
  buttonType: {
    type: String,
    default: "red"
  },
  center: {
    type: Boolean,
    default: false
  },
  responsibilityName: {
    type: String,
    default: ""
  }
});

const emit = defineEmits(["input", "onAction", "onClose"]);

// Always keep an internal open state so the dialog can visually close itself
// even if the parent doesn't immediately (or ever) update `value`.
const internalOpen = ref(props.value);
watch(
  () => props.value,
  (val) => {
    internalOpen.value = val;
  },
  { immediate: true }
);

// We keep the dialog mounted briefly on close so CSS can animate out.
const isRendered = ref(props.value);
const isShown = ref(props.value);
let closeTimer = null;

const syncAnimationState = (open) => {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }

  if (open) {
    isRendered.value = true;
    // Next frame so the browser can apply "closed" styles first.
    requestAnimationFrame(() => {
      isShown.value = true;
    });
  } else {
    isShown.value = false;
    closeTimer = setTimeout(() => {
      if (!internalOpen.value) isRendered.value = false;
      closeTimer = null;
    }, ANIMATION_MS);
  }
};

watch(
  internalOpen,
  (open) => {
    syncAnimationState(open);
  },
  { immediate: true }
);

const close = () => {
  internalOpen.value = false;
  // Keep emitting for compatibility with existing v-model/onClose listeners.
  emit("input", false);
  emit("onClose");
};

const onKeydown = (e) => {
  if (e.key === "Escape") close();
};

let previousBodyOverflow = null;
watch(
  isRendered,
  (rendered) => {
    if (rendered) {
      document.addEventListener("keydown", onKeydown);
      previousBodyOverflow = document?.body?.style?.overflow ?? "";
      if (document?.body) document.body.style.overflow = "hidden";
    } else {
      document.removeEventListener("keydown", onKeydown);
      if (document?.body && previousBodyOverflow !== null) {
        document.body.style.overflow = previousBodyOverflow;
      }
      previousBodyOverflow = null;
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
  document.removeEventListener("keydown", onKeydown);
  if (document?.body && previousBodyOverflow !== null) {
    document.body.style.overflow = previousBodyOverflow;
  }
});

const resolvedTitle = computed(() => props.title || "Cancel Inspection Request");

function escapeHtml(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const resolvedMessageHtml = computed(() => {
  if (props.message) return props.message;
  const name = props.responsibilityName
    ? escapeHtml(props.responsibilityName)
    : "this responsibility";
  return `You are about to cancel your request to be inspected on responsibility <span class="cancel-inspection-request-dialog__avatar-text">${name}</span>.`;
});
</script>
<style scoped>
.cancel-inspection-request-dialog {
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none;
}

.cancel-inspection-request-dialog__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(16, 24, 40, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  opacity: 0;
  transition: opacity 180ms ease;
  pointer-events: none;
}

.cancel-inspection-request-dialog--open {
  pointer-events: auto;
}

.cancel-inspection-request-dialog--open .cancel-inspection-request-dialog__backdrop {
  opacity: 1;
  pointer-events: auto;
}

.cancel-inspection-request-dialog__panel {
  max-width: 100%;
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 20px 40px rgba(16, 24, 40, 0.2);
  transform: translateY(10px) scale(0.98);
  opacity: 0;
  transition: transform 180ms ease, opacity 180ms ease;
  will-change: transform, opacity;
}

.cancel-inspection-request-dialog--open .cancel-inspection-request-dialog__panel {
  transform: translateY(0) scale(1);
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .cancel-inspection-request-dialog__backdrop,
  .cancel-inspection-request-dialog__panel {
    transition: none !important;
  }
}

.cancel-inspection-request-dialog__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.cancel-inspection-request-dialog__spacer {
  flex: 1;
}

.cancel-inspection-request-dialog__close-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #ffffff;
  padding: 0;
  cursor: pointer;
  margin-top: -20px;
}

.cancel-inspection-request-dialog__close-button:focus-visible {
  outline: 2px solid rgba(127, 86, 217, 0.6);
  outline-offset: 2px;
}

.cancel-inspection-request-dialog__content {
  margin-bottom: 32px;
  margin-top: 20px;
}

.cancel-inspection-request-dialog__footer {
  display: flex;
}

.cancel-inspection-request-dialog__popup {
  border-radius: 12px;
}
.cancel-inspection-request-dialog__popup .cancel-inspection-request-dialog__icon-wrapper {
  border: 1px solid #EAECF0;
  padding: 13px;
  border-radius: 10px;
  width: 48px;
  height: 48px;
  box-shadow: 0px 1px 2px 0px #1018280D;
}
.cancel-inspection-request-dialog__title {
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
}
.cancel-inspection-request-dialog__message {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #666666;
  text-align: left !important;
  width: 60%;
  margin: 0 auto;
}
.cancel-inspection-request-dialog__message.text-center {
  text-align: center !important;
}
.cancel-inspection-request-dialog__title.text-center {
  text-align: center !important;
}
.cancel-inspection-request-dialog__avatar-text {
  color: #7f56d9;
  line-height: 1.5rem;
  font-weight: 500;
}
.cancel-inspection-request-dialog--bg-pattern {
  background-image: url("@components/assets/Backgroundpatterndecorative_1.svg");
  background-position: right;
  background-repeat: no-repeat;
}
.cancel-inspection-request-dialog__cursor-pointer {
  cursor: pointer;
}
.cancel-inspection-request-dialog__button-disabled {
  background-color: #E0E0E0 !important;
}
.cancel-inspection-request-dialog__button.standard-button--full-width {
  height: 44px;
}
</style>